import type { PermissionsDto } from "@/client";

// What each list endpoint behind a filter control or an active-filter chip requires, as the
// handler's own @UsePermissions in edgify-backend declares it. The backend's PermissionsGuard reads
// it with getAllAndOverride, so a handler's list replaces its controller's: sites-tagging's
// class-level sites.tags.update, for one, does not apply to listing the tags.
//
// Any 403 sends the whole page to /dashboard/unauthorized (the response interceptor in
// client/index.ts), so a control or chip whose role lacks the permission must not ask at all. The
// chips (filter-chip-lookups.tsx) and the gated filter controls (withPermission) both read this one
// table, so the two can't drift apart.
//
// Import-free apart from the type, like filter-chip-label.ts, so it can be tested without the client.

export type PermissionCheck = (
  permissions: PermissionsDto,
) => boolean | undefined;

// Every path is optional-chained, so a permissions object that lacks a subtree -- a partial one, as
// tests pass -- reads as refused instead of throwing. Until the role's own permissions arrive,
// PermissionsProvider serves the all-false template, so every check but `me` refuses then too.
export const listPermissions = {
  // GET /sites/minimal
  sites: (p) => p?.sites?.backend?.readMinimal,
  // GET /sites/sites-tagging/tags
  siteTags: (p) => p?.sites?.backend?.readMinimal,
  // GET /sites/regions and /sites/subRegions -- the region option lists (ID-7057 phase 2)
  regions: (p) => p?.sites?.backend?.readMinimal,
  // GET /clusters/minimal
  clusters: (p) => p?.clusters?.backend?.readMinimal,
  // GET /devices/minimal
  devices: (p) => p?.devices?.backend?.readMinimal,
  // GET /devices/devices-tagging/tags
  deviceTags: (p) => p?.devices?.backend?.readMinimal,
  // GET /models/minimal
  models: (p) => p?.models?.backend?.readMinimal,
  // GET /site-groups/summaries -- site groups have no minimal list, nor a `backend` subtree
  siteGroups: (p) => p?.siteGroups?.list,
  // GET /tags -- the sample tags
  sampleTags: (p) => p?.samples?.backend?.readMinimal,
  // GET /samples/uxSource
  labelSources: (p) => p?.samples?.backend?.readDetails,
  // GET /sessions/sessions-tagging/tags
  sessionTags: (p) => p?.sessions?.backend?.readMinimal,
  // GET /catalog/catalog-tagging/tags
  catalogTags: (p) => p?.catalog?.backend?.readMinimal,
  // GET /catalog/labels -- the catalog items, named by PLU
  catalogLabels: (p) => p?.catalog?.backend?.readMinimal,
  // GET /catalog/families
  catalogFamilies: (p) => p?.catalog?.backend?.readMinimal,
  // GET /catalog/summaries -- the full catalog items, behind the Labels and Predictions filters
  catalogItems: (p) => p?.catalog?.backend?.readDetails,
  // GET /users/minimal
  users: (p) => p?.users?.backend?.readMinimal,
  // GET /users -- the full user list the Tagged-by filter loads
  userDetails: (p) => p?.users?.backend?.readDetails,
  // GET /accounts/minimal
  accounts: (p) => p?.accounts?.backend?.readMinimal,
  // GET /static-datasets/minimal
  datasets: (p) => p?.datasets?.backend?.readMinimal,
  // GET /alerts
  alerts: (p) => p?.alerts?.backend?.readDetails,
  // GET /pendingSampleActions/upload-operations
  uploadOperations: (p) => p?.pending?.backend?.readDetails,
  // GET /users/me needs no permission: every signed-in user can read their own record.
  me: () => true,
} satisfies Record<string, PermissionCheck>;
